import React, { useState } from 'react';
import { Box, Paper, Typography, Stack, Grid, TextField, Button, MenuItem, Chip, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { optionsService } from '../../services/optionsService';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const BacktestLabPage: React.FC = () => {
  const [strategy, setStrategy] = useState('SHORT_STRANGLE');
  const [from, setFrom] = useState('2023-01-01');
  const [to, setTo] = useState('2024-12-31');
  const [lots, setLots] = useState(1);
  const [isRunning, setIsRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const handleRun = async () => {
    setIsRunning(true);
    try {
      const res = await optionsService.runBacktest({
        strategy,
        startDate: from,
        endDate: to,
        lotCount: lots,
      });
      if (res && res.metrics) {
        setReport(res);
        setRunError(null);
        setHasRun(true);
      } else {
        setRunError('Backtest returned no metrics');
      }
    } catch (err: any) {
      setRunError(err?.message || 'Backtest run failed');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      {/* Backtest Parameters Form */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <ScienceIcon sx={{ color: '#0284C7' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
            NIFTY OPTIONS BACKTEST LAB
          </Typography>
        </Stack>

        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField select fullWidth size="small" label="Strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)}>
              <MenuItem value="BULL_PUT_CREDIT_SPREAD">Bull Put Credit Spread (7–20 DTE Hedged)</MenuItem>
              <MenuItem value="BEAR_CALL_CREDIT_SPREAD">Bear Call Credit Spread (7–20 DTE Hedged)</MenuItem>
              <MenuItem value="IRON_CONDOR">Iron Condor (7–20 DTE Hedged Wings)</MenuItem>
              <MenuItem value="SHORT_STRANGLE">Short Strangle (16Δ Legacy Baseline)</MenuItem>
              <MenuItem value="SHORT_STRADDLE">Short Straddle (ATM Legacy Baseline)</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.5 }}>
            <TextField fullWidth size="small" label="From Date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2.5 }}>
            <TextField fullWidth size="small" label="To Date" type="date" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <TextField fullWidth size="small" label="Lots" type="number" value={lots} onChange={(e) => setLots(Number(e.target.value))} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Button fullWidth variant="contained" startIcon={<PlayArrowIcon />} onClick={handleRun} disabled={isRunning} sx={{ bgcolor: '#0284C7', height: 40, fontWeight: 700 }}>
              {isRunning ? 'RUNNING...' : 'RUN BACKTEST'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {runError && (
        <Typography variant="body2" sx={{ color: '#DC2626', mb: 2 }}>
          {runError}
        </Typography>
      )}

      {/* Results Section */}
      {hasRun && report && (
        <>
          {/* Key Metrics Grid */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Total Trades
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  {report.metrics?.totalTrades ?? '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Win Rate
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#16A34A' }}>
                  {report.metrics?.winRate != null ? `${report.metrics.winRate.toFixed(2)}%` : '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Profit Factor
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0284C7' }}>
                  {report.metrics?.profitFactor != null ? report.metrics.profitFactor.toFixed(2) : '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Net Realized P&L
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: (report.metrics?.totalNetPnl ?? 0) >= 0 ? '#16A34A' : '#DC2626', fontFamily: "'Roboto Mono', monospace" }}>
                  {report.metrics?.totalNetPnl != null ? `${report.metrics.totalNetPnl >= 0 ? '+' : ''}₹${report.metrics.totalNetPnl.toLocaleString('en-IN')}` : '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Max Drawdown
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#DC2626' }}>
                  {report.metrics?.maxDrawdownPct != null ? `${report.metrics.maxDrawdownPct.toFixed(2)}%` : '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Sharpe Ratio
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0284C7' }}>
                  {report.metrics?.sharpeRatio != null ? report.metrics.sharpeRatio.toFixed(2) : '-'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Equity Curve */}
          {Array.isArray(report.equityCurve) && report.equityCurve.length > 1 && (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>
                CUMULATIVE EQUITY CURVE ({from} – {to})
              </Typography>
              <Box sx={{ width: '100%', bgcolor: '#F8FAFC', p: 2, borderRadius: 1.5, border: '1px solid #E2E8F0' }}>
                {(() => {
                  const points = report.equityCurve as Array<{ equity: number }>;
                  const values = points.map((p) => p.equity);
                  const min = Math.min(...values);
                  const max = Math.max(...values);
                  const range = max - min || 1;
                  const w = 700;
                  const h = 160;
                  const path = points
                    .map((p, i) => {
                      const x = (i / (points.length - 1)) * w;
                      const y = h - 20 - ((p.equity - min) / range) * (h - 40);
                      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
                    })
                    .join(' ');
                  const last = values[values.length - 1];
                  return (
                    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
                      <line x1="0" y1={h - 20} x2={w} y2={h - 20} stroke="#CBD5E1" strokeWidth="1" strokeDasharray="3 3" />
                      <path d={path} fill="none" stroke={last >= values[0] ? '#10B981' : '#DC2626'} strokeWidth="3" />
                    </svg>
                  );
                })()}
              </Box>
            </Paper>
          )}

          {/* Trade Log */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>
              TRADE LOG ({report.trades?.length ?? 0} TRADES)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC', '& th': { fontWeight: 600, color: '#64748B', fontSize: '0.72rem' } }}>
                  <TableCell>TRADE ID</TableCell>
                  <TableCell>DATE</TableCell>
                  <TableCell>REGIME</TableCell>
                  <TableCell align="right">ENTRY PREM</TableCell>
                  <TableCell align="right">EXIT PREM</TableCell>
                  <TableCell align="right">NET P&L</TableCell>
                  <TableCell>EXIT REASON</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(report.trades || []).slice(0, 50).map((t: any) => (
                  <TableRow key={t.tradeId} hover sx={{ '& td': { fontSize: '0.78rem' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#0284C7' }}>{t.tradeId}</TableCell>
                    <TableCell>{new Date(t.entryTime).toISOString().substring(0, 10)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t.marketRegime} sx={{ fontSize: '0.65rem', height: 20 }} />
                    </TableCell>
                    <TableCell align="right">₹{t.entryPremium?.toFixed(1)}</TableCell>
                    <TableCell align="right">₹{t.exitPremium?.toFixed(1)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: t.netPnl >= 0 ? '#16A34A' : '#DC2626' }}>
                      {t.netPnl >= 0 ? '+' : ''}₹{t.netPnl?.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={t.exitReason} sx={{ fontSize: '0.65rem', height: 20, bgcolor: t.netPnl >= 0 ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)', color: t.netPnl >= 0 ? '#16A34A' : '#DC2626' }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default BacktestLabPage;
